/**
 * @fileoverview SLS Web 端日志采集核心类
 *
 * 设计要点（相对原 web/accounts 实现的改造）：
 * 1. **去 app 硬编码**：source/topic/envConfig/allowLog/IS_DEV/VITE_APP_VERSION/auth 全部由
 *    {@link LoggerConfig} 注入，core 对任何 app 零依赖。
 * 2. **钩子系统外置**：由独立的 {@link HookSystem} 承载，send 时收集钩子数据合并入 data。
 * 3. **插件按需**：perf / localLogger 不传配置即不启动，不增加包体积。
 * 4. **error 字符串化统一**：原 accounts 缺失的 stringifyErrorValues 在 core 统一启用。
 * 5. **SDK 静态 bundle**：SLS SDK 在构建期内化进 dist（方案 A），消费者无需配 patch 即开箱即用。
 *
 * 发送策略：
 * - error：立即发送（immediate）
 * - 其他：2s 内超过 3 条触发批量；批量队列满 100 条或 2s 超时则 flush
 *
 * 缓冲机制：
 * - 初始化前的日志进缓冲区（容量 100，30s 过期清理）
 * - 页面卸载时 flush 剩余日志
 */
/* eslint-disable no-console */
/**
 * SLS SDK 静态导入（构建期由 bundler 内化进 dist，消费者无需 patch 即开箱即用）。
 * @description 历史：曾用动态 import 做懒加载；改为方案 A（bundle）后用静态导入，
 *   让已 patch 的 SDK 运行时代码随本包发布，消除消费者的 patch 配置负担。
 */
import SlsTracker from '@aliyun-sls/web-track-browser'
import { genId, getClientId, stringifyErrorValues } from '@peerup/utils'
import dayjs from 'dayjs'

import type { CommonErrorEventMap, CommonInfoEventMap, CommonWarnEventMap } from '../actions/common'
import type { SlsTrackerLike, SlsTrackerOptions } from './slsTypes'
import type {
  BaseUserInfo,
  LogActionHook,
  LoggerConfig,
  LogItem,
  LogLevel,
  SendOptions,
  SlsConfig,
} from './types'

import { CommonInfoAction } from '../actions/common'
import { HookSystem } from '../hooks'
import { LocalLogger } from '../plugins/LocalLogger'
import { createMemoryHandler, createVisibilityHandler } from './baseHandle'
import { cloneData, liteFetch } from './inlineUtils'

const TYPE_EMOJI: Record<LogLevel, string> = {
  log: '🚀',
  warn: '⚠️',
  error: '🚨',
  info: '📝',
}

// SDK 静态导入按构造器断言，消除原生包类型在 patch 前后的差异（见 slsTypes.ts）
const SlsTrackerCtor = SlsTracker as unknown as new (opt: SlsTrackerOptions) => SlsTrackerLike

/** 忽略控制台打印的 action（高频/噪音日志） */
const IGNORE_ACTIONS: Array<string | number> = ['XMLHttpRequest']

const MAX_BUFFER_SIZE = 100 // 最大缓存条数
const BUFFER_TIMEOUT = 30000 // 缓存超时时间(ms)
const BATCH_INTERVAL = 2000 // 批量发送间隔(ms)
const BATCH_THRESHOLD = 3 // 触发批量发送的阈值
const BATCH_MAX_SIZE = 100 // 批量发送最大数

/**
 * SLS 日志服务封装类。
 * 由 {@link createLogger} 实例化，消费方直接使用其实例（$logger.info / $logger.error ...）。
 */
export class AliWebLogger<
  TWarnMap extends CommonWarnEventMap = CommonWarnEventMap,
  TErrorMap extends CommonErrorEventMap = CommonErrorEventMap,
  TInfoMap extends CommonInfoEventMap = CommonInfoEventMap,
> {
  /** 会话 id（不带横杠，便于检索） */
  private readonly sid = genId()

  /** SLS tracker 实例（SDK 加载完成后赋值） */
  private tracker?: SlsTrackerLike

  /** 当前用户信息（authKitPromise resolve 后填充） */
  private userInfo: BaseUserInfo = {}

  /** 设备 id（getClientId resolve 后填充） */
  public deviceId = ''

  /** 本地日志（IndexedDB，仅在 plugins.localLogger 配置时才实例化） */
  private localLogger?: LocalLogger

  /** 钩子系统 */
  private readonly hooks: HookSystem

  /** 日志缓冲区（初始化前缓存） */
  private logBuffer: LogItem[] = []
  private cleanupTimer?: ReturnType<typeof setInterval>

  /** 批量发送队列 */
  private batchQueue: LogItem[] = []
  private batchTimer?: ReturnType<typeof setTimeout>
  private sendCount = 0
  private lastSendTime = 0

  private readonly abortController = new AbortController()

  /** 是否已初始化完成（SDK + auth 就绪） */
  private isInit = false

  /** 是否已销毁 */
  public isDestroyed = false

  /** 页面可见性状态 */
  private vs: 0 | 1 = Number(document.visibilityState !== 'hidden') as 0 | 1

  private readonly ua = window.navigator.userAgent

  /** 各类自动上报的清理函数（destroy 时调用） */
  private cleanups: Array<() => void> = []

  /** perf 插件句柄（启动/断开），由 createLogger 注入 */
  private perfController?: {
    init: (logger: AliWebLogger) => void
    disconnect: () => void
  }

  constructor(private readonly config: LoggerConfig<TWarnMap, TErrorMap, TInfoMap>) {
    const isDev = config.isDev ?? false
    this.hooks = new HookSystem(isDev)
    // localLogger 仅在显式配置 plugins.localLogger 时才实例化，否则用 no-op stub
    // LocalLogger 只读 env/staticUrl，擦除泛型传入
    if (config.plugins?.localLogger) {
      this.localLogger = new LocalLogger(config as LoggerConfig)
    }

    this.#init()

    // deviceId 异步填充（getClientId 通常从 localStorage 读取，较快，不阻塞 init）
    getClientId().then(id => {
      this.deviceId = id
    })
  }

  /**
   * 注入 perf 插件控制器（由 createLogger 在动态 import 完成后调用）
   * @description createLogger 里 perf SDK 是异步加载的，注入时 #init 通常已执行完，
   *   故需在此判断：若已过了初始化节点（this.perfController 之前为 undefined 表示首次注入），
   *   且当前应启动，则立即 init。
   */
  /**
   * 注入 perf 插件控制器（由 createLogger 在动态 import 完成后调用）
   * @internal
   */
  setPerfController(controller: { init: (logger: AliWebLogger) => void; disconnect: () => void }) {
    this.perfController = controller
    // 兜底启动：#init 早已同步执行完，注入时若满足启动条件则立即 init
    // 启动条件与 #init 一致：过了首个 allowLog/IS_DEV 判断（即 allowLog || isDev）
    const allowLog = this.config.allowLog?.() ?? false
    const isDev = this.config.isDev ?? false
    if (allowLog || isDev) {
      controller.init(this)
    }
  }

  #init() {
    this.#registerLifecycleEvents()

    const allowLog = this.config.allowLog?.() ?? false
    const isDev = this.config.isDev ?? false

    // 未配置 allowLog 时给出明确提示，避免日志静默丢失（详见 #send 与 #init 的默认值一致）
    if (!this.config.allowLog && !isDev) {
      console.warn('[🚨Logger] 未配置 allowLog，默认不上报到 SLS。若需上报请在 createLogger 时传入 allowLog。')
    }

    // 自动业务上报：可见性切换 + 内存/DOM 监控（对齐旧实现，无条件启动）
    this.cleanups.push(createMemoryHandler(this))

    // 对齐旧 web #init：prod 且不允许上报则整体退出（perf 也不启动）
    if (!allowLog && !isDev) return

    // perf 启动：过了上面的判断即启动（dev 下也启动，数据走控制台打印）
    if (this.perfController) {
      this.perfController.init(this)
    }

    // dev 环境不连 SLS（对齐旧 web：if (IS_DEV) return）
    if (isDev) return

    this.#initLogger().catch(console.error)
    this.#startCleanupTimer()
  }

  #lastVisibleChangeTime = Date.now()

  #registerLifecycleEvents() {
    const onVisibilitychange = () => {
      createVisibilityHandler(this)()
      this.vs = Number(document.visibilityState !== 'hidden') as 0 | 1
      const duration = Date.now() - this.#lastVisibleChangeTime
      this.#lastVisibleChangeTime = Date.now()
      this.log('页面可见状态切换', { vs: this.vs, duration })
    }
    const onUnload = () => {
      this.log('页面即将关闭', {}, { immediate: true })
      this.destroy()
    }
    const onBeforeUnload = () => {
      this.log('beforeunload 页面即将关闭', {}, { immediate: true })
      const { jsHeapSizeLimit, usedJSHeapSize } = window.performance.memory || {}
      // PAGE_UNLOAD 属 CommonInfoAction（公共上报项），TInfoMap 约束保证含此 key
      this.info(CommonInfoAction.PAGE_UNLOAD, { usedJSHeapSize, jsHeapSizeLimit })
    }

    window.addEventListener('visibilitychange', onVisibilitychange, { signal: this.abortController.signal })
    window.addEventListener('unload', onUnload, { signal: this.abortController.signal })
    window.addEventListener('beforeunload', onBeforeUnload, { signal: this.abortController.signal })
  }

  /** 获取用户信息（含设备/会话，调试用） */
  getBaseInfo() {
    return {
      ...this.userInfo,
      deviceId: this.deviceId,
      sid: this.sid,
    }
  }

  #startCleanupTimer = () => {
    this.cleanupTimer = setInterval(() => {
      const now = Date.now()
      this.logBuffer = this.logBuffer.filter(log => now - log._t < BUFFER_TIMEOUT)
    }, BUFFER_TIMEOUT / 2)
  }

  #addToBuffer = (log: LogItem, immediate?: boolean) => {
    if (immediate) {
      this.logBuffer.unshift(log)
    } else {
      this.logBuffer.push(log)
    }
    // 超出最大缓存，移除最早的非立即日志
    if (this.logBuffer.length > MAX_BUFFER_SIZE) {
      const idx = this.logBuffer.findIndex(item => !item.immediate)
      if (idx !== -1) {
        this.logBuffer.splice(idx, 1)
      }
    }
  }

  #flushBuffer = () => {
    if (!this.isInit || !this.tracker || this.logBuffer.length === 0) return

    const immediateLogs: LogItem[] = []
    const normalLogs: LogItem[] = []
    for (const log of this.logBuffer) {
      ;(log.immediate ? immediateLogs : normalLogs).push(log)
    }

    // 立即发送的日志逐条发送
    for (const log of immediateLogs) {
      this.#send(log.type, log.action, log.data, { immediate: true })
    }

    // 普通日志批量发送
    if (normalLogs.length > 0) {
      const info = this.getBaseInfo()
      try {
        this.tracker.sendBatchLogs(
          normalLogs.map(i => ({
            ...i,
            uid: (info.userId as string) || '',
            mguid: (info.memberGuid as string) || '',
            guid: (info.orgGuid as string) || '',
            deviceId: this.deviceId,
            username: (info.userName as string) || '',
          })),
        )
      } catch (e) {
        console.error('[🚨Logger] Failed to send batch logs:', e)
      }
    }
    this.logBuffer = []
  }

  /** 初始化 SLS tracker */
  #initLogger = async () => {
    // sls 内聚字段优先，兜底取平铺字段（向后兼容旧写法）
    const sls: Partial<SlsConfig> = this.config.sls ?? {}
    const source = sls.source ?? this.config.source
    const topic = sls.topic ?? this.config.topic ?? 'peerup-fe'
    const tracker = sls.tracker ?? this.config.tracker
    const trackerOptions = sls.trackerOptions ?? this.config.trackerOptions
    const innerLoggerEnable = sls.innerLoggerEnable ?? this.config.innerLoggerEnable
    const innerLoggerEndpoint = sls.innerLoggerEndpoint ?? this.config.innerLoggerEndpoint

    if (tracker) {
      const baseOptions: SlsTrackerOptions = {
        host: tracker.host,
        project: tracker.project,
        logstore: tracker.logstore,
        source,
        topic,
        ...trackerOptions,
      }

      // 内部 Loki 网关覆盖
      if (innerLoggerEnable) {
        const endpoint = innerLoggerEndpoint ?? '/api/observer-x/loki/push'
        Object.assign(baseOptions, {
          assemblePayload(arr: object[]) {
            return arr.map(e => ({
              ...e,
              _t: Number((e as LogItem)._t),
              __source__: source,
              __topic__: topic,
            }))
          },
          url: endpoint,
          sendPayload: liteFetch,
        })
      }

      this.tracker = new SlsTrackerCtor(baseOptions)
    }

    // 等待用户信息就绪后再标记初始化完成（与旧实现 await authKitPromise + isReady 对齐）
    // 否则 flush 缓冲区时 userInfo 为空，日志的用户字段（uid/mguid/guid/username）全丢
    if (this.config.authKitPromise) {
      try {
        const info = await this.config.authKitPromise()
        if (info) this.userInfo = info
      } catch (e) {
        console.error('[🚨Logger] authKitPromise rejected:', e)
      }
    }

    this.isInit = true
    console.log('[🚀Logger] sdk init success!')

    // 初始化完成后，发送缓冲区中的日志（此时 userInfo 已就绪）
    this.#flushBuffer()
  }

  #addToBatchQueue = (logData: LogItem) => {
    this.batchQueue.push(logData)
    if (this.batchQueue.length >= BATCH_MAX_SIZE) {
      this.#flushBatchQueue()
      return
    }
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(this.#flushBatchQueue, BATCH_INTERVAL)
    }
  }

  #flushBatchQueue = () => {
    if (this.batchQueue.length === 0) return
    try {
      this.tracker?.sendBatchLogs(this.batchQueue)
    } catch (e) {
      console.error('[🚨Logger] Failed to send batch logs:', e)
    }
    this.batchQueue = []
    if (this.batchTimer) {
      clearTimeout(this.batchTimer)
      this.batchTimer = undefined
    }
    this.sendCount = 0
    this.lastSendTime = Date.now()
  }

  /** 核心：组装并发送单条日志 */
  #send = async (
    type: LogLevel,
    action: string | number,
    data: unknown,
    options: SendOptions = {},
  ): Promise<void> => {
    const info = this.getBaseInfo()
    const logData: LogItem = {
      v: this.config.appVersion,
      type,
      action,
      uid: (info.userId as string) || '',
      mguid: (info.memberGuid as string) || '',
      guid: (info.orgGuid as string) || '',
      deviceId: this.deviceId,
      username: (info.userName as string) || '',
      ua: this.ua,
      tt: ~~performance.now(),
      data:
        typeof data === 'string'
          ? { message: data }
          : (this.config.transformData?.(data) ?? cloneData(data)),
      vs: this.vs,
      _t: Date.now(),
      url: window.location.href,
      title: document.title,
      sid: this.sid,
    }

    // 执行钩子，收集额外数据合并入 data
    if (this.hooks.has(action)) {
      const hookExtra = await this.hooks.run(action, logData)
      if (Object.keys(hookExtra).length > 0) {
        const base = typeof logData.data === 'object' && logData.data !== null ? logData.data : {}
        logData.data = { ...base, ...hookExtra }
      }
    }
    // 深度遍历 data，将 Error 属性转字符串（不改变结构/引用）
    logData.data = stringifyErrorValues(logData.data as object)

    const { ignoreLog = false, immediate = false } = options
    const isDev = this.config.isDev ?? false
    if (isDev && !ignoreLog && !IGNORE_ACTIONS.includes(action)) {
      console[type](
        `[${TYPE_EMOJI[type]}${type}|${dayjs(logData._t).format('HH:mm:ss.SSS')}] ${action}`,
        logData.data,
      )
    }
    this.localLogger?.[type](action, logData)

    const allowLog = this.config.allowLog?.() ?? false
    if (!allowLog || this.isDestroyed) return

    // 未初始化则进缓冲区
    if (!this.isInit || !this.tracker) {
      this.#addToBuffer(logData, immediate)
      return
    }

    // 批量频率控制
    const now = Date.now()
    if (now - this.lastSendTime < BATCH_INTERVAL) {
      this.sendCount += 1
      if (this.sendCount >= BATCH_THRESHOLD) {
        this.#addToBatchQueue(logData)
        return
      }
    } else {
      this.sendCount = 1
      this.lastSendTime = now
    }

    try {
      this.tracker.send(logData)
    } catch (e) {
      console[type]('[🚀Logger] Failed to send log:', e)
    }
  }

  // ========== 对外 API ==========
  // 普通方法语法（非箭头函数属性）：本类方法均通过 logger.info()/this.info() 形式调用，
  // 从不作为裸函数引用（无解构/回调传递），普通方法的 this 绑定完全正确。

  /**
   * 记录普通日志（松散类型）
   * @description 一般用于记录用户行为的常规日志
   * @param action 日志动作
   * @param data 日志数据，可以是字符串(message)或对象
   * @param options 日志选项
   * @param options.ignoreLog 是否忽略控制台打印
   * @param options.immediate 是否立即发送
   */
  public log(action: string, data: object | string = {}, options: SendOptions = {}): void {
    void this.#send('log', action, data, options)
  }

  /**
   * 记录警告日志
   * @description 一般用于记录关键的警告信息，可能引发错误的警告
   */
  public warn<A extends keyof TWarnMap & string>(action: A, data: TWarnMap[A]): void {
    void this.#send('warn', action, data)
  }

  /**
   * 记录错误日志，立即发送
   * @description 一般用于上报某些不可修复的错误，需要上报一些错误信息
   */
  public error<A extends keyof TErrorMap & string>(action: A, data: TErrorMap[A]): void {
    void this.#send('error', action, data, { immediate: true })
  }

  /**
   * 记录信息日志（不常用）
   * @description 一般用于记录一些关键信息，或一些非关键的日志
   */
  public info<A extends keyof TInfoMap & string>(action: A, data: TInfoMap[A], options: SendOptions = {}): void {
    void this.#send('info', action, data, options)
  }

  /**
   * 订阅指定 action 的日志钩子
   * @description 当指定 action 的日志被上报时，会执行钩子函数，并将返回值合并到日志数据中
   * @param action 要监听的日志动作，支持单个或多个
   * @param hook 钩子函数，接收日志数据（只读），返回需要合并的额外字段
   * @param description 订阅描述（可选，便于调试）
   * @returns 返回一个取消订阅的函数
   * @example 订阅单个 action
   * ```ts
   * const unsubscribe = $logger.on(InfoAction.PAGE_STUCK, (logData) => ({
   *   docId: getCurrentDocId(),
   *   blockCount: getBlockCount()
   * }), '文档卡顿上下文')
   *
   * unsubscribe() // 取消订阅
   * ```
   * @example 订阅多个 action
   * ```ts
   * // 一次性为多个卡顿 action 注入相同的上下文
   * const unsubscribe = $logger.on(
   *   [InfoAction.PAGE_STUCK, InfoAction.PAGE_LONG_STUCK, InfoAction.PAGE_RUN_STUCK],
   *   () => ({
   *     docId: docId.value,
   *     blockCount: getBlockCount()
   *   }),
   *   '卡顿日志上下文'
   * )
   * ```
   */
  public on(action: string | number | Array<string | number>, hook: LogActionHook, description?: string): () => void {
    return this.hooks.on(action, hook, description)
  }

  /**
   * 取消订阅指定 action 的日志钩子
   * @param action 日志动作，支持单个或多个
   * @param hook 要移除的钩子函数
   */
  public off(action: string | number | Array<string | number>, hook: LogActionHook): void {
    this.hooks.off(action, hook)
  }

  /**
   * 清除指定 action 的所有钩子
   * @param action 日志动作，支持单个或多个，不传则清除所有钩子
   */
  public clearHooks(action?: string | number | Array<string | number>): void {
    this.hooks.clear(action)
  }

  /**
   * 销毁实例
   */
  public destroy(): void {
    if (this.cleanupTimer) clearInterval(this.cleanupTimer)
    if (this.batchTimer) clearTimeout(this.batchTimer)
    this.isDestroyed = true
    this.abortController.abort()
    // 🔥 清理 perf 观察器
    this.perfController?.disconnect()
    // 🔥 清理自动上报的定时器/监听
    for (const cleanup of this.cleanups) cleanup()
    this.cleanups = []
    // 发送剩余日志（必须在 isInit 置 false 之前执行，flushBuffer 依赖 isInit 判断）
    this.#flushBuffer()
    this.#flushBatchQueue()
    // 标记初始化已失效：destroy 之后再调 log() 会因 isInit=false 且 cleanupTimer 已清而不再静默积累
    this.isInit = false
  }
}
